import { Fragment, createElement } from "react";
import { NONE, OptionalDataStore, call, getRequired, logError, mapSequence, runSequence } from "shelving";
import { useStore } from "shelving/react";
import { AUTH, logout } from "shared/firebase/auth";
import { NOTICES } from "shared/react";
import { DB } from "./db.js";

/** Store details about the logged-in user. */
export const USER = new OptionalDataStore<{ uid: string; username: string }>(NONE);
runSequence(
	USER.through(
		mapSequence(AUTH, async auth => {
			if (auth) {
				try {
					// Preload the profile and users documents (must exist otherwise this login fails).
					const { uid } = auth;
					const { username } = getRequired(await DB.getItem("profile", uid));
					return { uid, username };
				} catch (thrown) {
					// There was a problem loading the user.
					// Probably a document with the UID didn't exist in either the `profiles` or `users` collections.
					NOTICES.error("Invalid user");
					logError(thrown);

					// Log the auth user out — they don't have corresponding database records.
					call(logout);
				}
			}
		}),
	),
);

/** Shared props for <LoggedIn> and <Loggedout> */
type LoggedProps = { children: React.ReactNode };

/** LoggedIn component only renders children if the auth user is logged in (i.e. the state is any truthy value). */
export function LoggedIn(props: LoggedProps): React.ReactElement | null {
	useStore(USER);
	return USER.value ? createElement(Fragment, props) : null;
}

/** LoggedOut component only renders children if the auth user is logged out (i.e. the state is null). */
export function LoggedOut(props: LoggedProps): React.ReactElement | null {
	useStore(USER);
	return !USER.value ? createElement(Fragment, props) : null;
}
